import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useState, useEffect, useRef } from "react";
import { GetCoinsContent } from "./GetCoinsContent";

interface GetCoinsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GetCoinsModal = ({ isOpen, onClose }: GetCoinsModalProps) => {
  // Swipe to close state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Swipe to close handlers
  const handleDragHandleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    const touch = e.touches[0];
    dragStartY.current = touch.clientY;
    setIsDragging(true);
  };

  const handleDragHandleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    const deltaY = touch.clientY - dragStartY.current;
    
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  };

  const handleDragHandleTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.stopPropagation();
    
    const threshold = 100;
    if (dragY > threshold) {
      onClose();
    } else {
      setDragY(0);
    }
    
    setIsDragging(false);
    dragStartY.current = 0;
  };

  const handleHeaderTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch.clientY < 60) {
      handleDragHandleTouchStart(e);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setDragY(0);
      setIsDragging(false);
    }
  }, [isOpen]);

  // Prevent pull-to-refresh when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store original values
      const originalOverscrollBehavior = document.body.style.overscrollBehaviorY;
      const originalOverscrollBehaviorHtml = document.documentElement.style.overscrollBehaviorY;
      
      // Prevent pull-to-refresh on body and html
      document.body.style.overscrollBehaviorY = 'none';
      document.documentElement.style.overscrollBehaviorY = 'none';
      
      return () => {
        // Restore original values
        document.body.style.overscrollBehaviorY = originalOverscrollBehavior;
        document.documentElement.style.overscrollBehaviorY = originalOverscrollBehaviorHtml;
      };
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      // Only allow closing, not opening via Dialog's onOpenChange
      if (!open) {
        onClose();
      }
    }}>
      <DialogContent 
        ref={modalContentRef}
        className="!fixed !left-0 !top-0 !right-0 !bottom-0 !w-[100vw] !h-[100vh] !max-w-none !max-h-none lg:!left-[50%] lg:!top-[50%] lg:!right-auto lg:!bottom-auto lg:!w-[95vw] lg:!max-w-5xl lg:!h-[92vh] lg:!max-h-[900px] m-0 p-0 rounded-none lg:rounded-2xl border-0 lg:border lg:border-border bg-background overflow-y-auto overflow-x-hidden !translate-x-0 translate-y-0 lg:!translate-x-[-50%] lg:!translate-y-[-50%] flex flex-col [&>button]:hidden pt-[env(safe-area-inset-top)] lg:pt-0 pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pb-0 dialog-content-base lg:shadow-2xl lg:shadow-black/50"
        style={{
          '--tw-translate-y': dragY > 0 ? `${dragY}px` : '0px',
          opacity: dragY > 0 ? Math.max(0.6, 1 - dragY / 400) : undefined,
          transition: isDragging ? 'none' : 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          overscrollBehaviorY: 'none',
        } as React.CSSProperties}
        aria-describedby={undefined}
      >
        {/* Drag Handle Indicator - Mobile & Tablet Only */}
        <div 
          className="lg:hidden pt-3 pb-2 flex justify-center touch-none cursor-grab active:cursor-grabbing select-none"
          onTouchStart={handleDragHandleTouchStart}
          onTouchMove={handleDragHandleTouchMove}
          onTouchEnd={handleDragHandleTouchEnd}
        >
          <div 
            className="w-10 h-1.5 bg-muted-foreground/50 rounded-full transition-all duration-200"
            style={{
              opacity: isDragging ? 0.6 : 1,
              transform: isDragging ? 'scaleX(0.6)' : 'scaleX(1)',
              backgroundColor: isDragging ? 'rgba(128, 128, 128, 0.7)' : undefined,
              transition: isDragging ? 'none' : 'all 0.2s ease',
            }}
          />
        </div>

        {/* Touch area for header drag */}
        <div 
          className="contents"
          onTouchStart={handleHeaderTouchStart}
          onTouchMove={handleDragHandleTouchMove}
          onTouchEnd={handleDragHandleTouchEnd}
        >
          <GetCoinsContent onClose={onClose} isPage={false} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
